import { Data, Modal as ModalModule, UI, Utils } from 'common';
import includes from 'lodash/includes';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import moment from 'moment';
import React, { Component } from 'react';
import CreateScanFormModal from '../scanForm/CreateScanFormModal.component';
import HistoryCommunication from '../utils/HistoryCommunication';

const { Modal, ModalButtonActionType } = ModalModule;
const { WrappedInputTextWithLabel, WrappedDropdownWithLabel } = UI;
const { Session, withLayoutAndModalContexts, Util, Analytics } = Utils;
const { Carrier, States, Capability } = Data;

// Modal view options
const ViewType = {
	Pickup: 'Pickup',
	PostPickup: 'PostPickup',
};

class ScheduleUspsPickupModal extends Component {
	constructor(props) {
		super(props);

		const { customerDetails = {} } = Session.getAccountInfo();
		const { address = {} } = customerDetails;

		this.state = {
			// Contact information
			addressLine1: address.addressLine1 || '',
			city: address.city || '',
			companyName: address.companyName || '',
			stateProvince: address.stateProvince || '',
			postalCode: address.postalCode || '',
			fullName: address.name || '',
			phone: address.phone || '',
			pickupInstructions: '',

			// Post Pickup Information
			pickupId: '', // confirmation number
			pickupDate: null,
			numberOfItemsInPickup: 0,

			// Required fields for validation
			validations: {
				// 0 = untouched, -1 = failed, 1 = valid
				fullName: 0,
				addressLine1: 0,
				postalCode: 0,
				city: 0,
				stateProvince: 0,
				phone: 0,
				pickupInstructions: 0,
			},
			viewType: ViewType.Pickup,
			isSchedulePickupClicked: false,
		};
	}

	buildSchedulePickupPayload = () => {
		const {
			addressLine1,
			city,
			companyName,
			fullName,
			stateProvince,
			postalCode,
			phone,
			pickupInstructions,
		} = this.state;

		/* Pickup date on Friday, then add 3 days and Pickup date on Saturday, then add 2 days to
		pickup next business day. Otherwise, pass the next day for USPS */

		const currentDay = moment().day();

		const pickupDate =
			currentDay === 6
				? `${moment()
						.add(currentDay === 2, 'days')
						.format('YYYY-MM-DD')}`
				: `${moment().add(1, 'days').format('YYYY-MM-DD')}`;

		return {
			pickup_address: {
				address_line1: addressLine1,
				city,
				state_province: stateProvince,
				postal_code: postalCode,
				country_code: 'US',
				company_name: companyName,
				phone,
				name: fullName,
			},
			carrier: Carrier.USPS,

			// USPS doesn't allow scheduling a specific time range.
			// The pickup window will be anytime in the next day
			pickup_window: {
				start_at: `${pickupDate}T00:00:00`,
				end_at: `${pickupDate}T23:59:00`,
			},
			pickup_instructions: pickupInstructions,
		};
	};

	/**
	 * Validate the required fields in the form
	 * @returns {boolean} false if any of the required field is -1
	 */
	validateForm = () => {
		const {
			fullName,
			addressLine1,
			postalCode,
			city,
			stateProvince,
			phone,
			pickupInstructions,
		} = this.state;

		const validations = {
			addressLine1: addressLine1.length === 0 ? -1 : 1,
			city: city.length === 0 ? -1 : 1,
			fullName: fullName.length === 0 ? -1 : 1,
			phone: phone.length === 0 ? -1 : 1,
			stateProvince: stateProvince.length === 0 ? -1 : 1,
			postalCode: postalCode.length === 0 ? -1 : 1,
			pickupInstructions: pickupInstructions.length === 0 ? -1 : 1,
		};

		this.setState({
			validations,
		});

		return includes(validations, -1) === false;
	};

	/**
	 * Check if the account has GlobalPost Print enabled
	 * @returns {boolean}
	 */
	isEligibleForGlobalPost = () =>
		Session.hasCapability(Capability.Globalpost) ||
		Session.hasCapability(Capability.GlobalPostPlus);

	/**
	 * Validate the field and update the validations state
	 * @param {object} event object
	 */
	onBlur = ({ target }) => {
		const { name, value } = target;

		this.setState(({ validations }) => {
			if (validations.hasOwnProperty(name)) {
				return {
					validations: {
						...validations,
						// it's invalid when value is an empty string
						[name]: value.length === 0 ? -1 : 1,
					},
				};
			}
		});
	};

	/**
	 * Validate "state" field and update the validations state
	 * Currently, we are not able to get "name" or "value" from its "onBlur" event.
	 * specific logic is added here until we have a better approach.
	 * @param {object} event object
	 */
	onBlurDropdown = () => {
		this.setState(({ validations, stateProvince }) => ({
			validations: {
				...validations,
				stateProvince: stateProvince === '' ? -1 : 1,
			},
		}));
	};

	/**
	 * Change the value of the fields
	 * @param {object} event object
	 */
	onChange = ({ target }) => {
		const { name, value } = target;

		this.setState({
			[name]: name === 'phone' ? Util.removePhoneFormatting(value) : value,
		});
	};

	/**
	 * onClick handler for "Schedule Pickup" button in Pickup modal view
	 */
	onClickSchedulePickupButton = async () => {
		const isValid = this.validateForm();
		const {
			modalContext: { showError, closeModal },
			modalId,
		} = this.props;

		if (isValid) {
			this.setState({
				isSchedulePickupClicked: true,
			});
			const params = this.buildSchedulePickupPayload();
			const response = await HistoryCommunication.schedulePickup(params);

			if (!HistoryCommunication.isSuccessful(response)) {
				this.setState({
					isSchedulePickupClicked: false,
				});

				const error = {
					errorCode: response?.error?.errorCode,
					errorMessage: isString(response?.error?.errorMessage)
						? response.error.errorMessage
						: response.ErrorDescription,
				};

				Analytics.trackEvent(
					'carrierPickup',
					{
						carrier: params.carrier,
						success: false,
						error: response,
					},
					Analytics.agent.segment
				);

				return showError({
					data: {
						// display the error code
						code:
							isNumber(error.errorCode) && !isNaN(error.errorCode)
								? error.errorCode
								: null,
						title: !isNumber(error.errorCode) ? 'Error' : null,
						description: `<p>${error.errorMessage}</p>`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
					closeCallback: () => {
						closeModal(modalId);
					},
				});
			}

			const data = Util.toCamelCase(response.data);
			const pickupDate = data.pickupWindow
				? moment(data.pickupWindow.startAt).format('dddd, MM/DD/YYYY')
				: '';

			Analytics.trackEvent(
				'carrierPickup',
				{
					carrier: params.carrier,
					success: true,
				},
				Analytics.agent.segment
			);

			this.setState({
				viewType: ViewType.PostPickup,
				pickupId: data.pickupId,
				pickupDate,
				numberOfItemsInPickup: data.numberOfItemsInPickup,
			});
		}
	};

	onClickPrintScanForm = () => {
		const {
			modalContext: { showModal, closeAll },
			data = {},
		} = this.props;
		const { fetchPrints = {}, selectedPrints = [], onRefreshGridAPI } = data;

		// show SCAN Form modal to get all eligiblePrints or selectedPrints
		closeAll(() => {
			showModal({
				component: CreateScanFormModal,
				data:
					selectedPrints.length > 0
						? {
								isForAllEligible: false,
								selectedPrints,
								onRefreshGridAPI,
							}
						: {
								isForAllEligible: true,
								fetchPrints,
								onRefreshGridAPI,
							},
			});
		});
	};

	/**
	 * Render the 'Pickup' modal view.
	 * @returns {ReactElement}
	 */
	renderPickupView = () => {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const {
			validations,
			fullName,
			phone,
			companyName,
			addressLine1,
			city,
			stateProvince,
			postalCode,
			pickupInstructions,
		} = this.state;

		return (
			<>
				<div className="p-grid">
					<p className="p-col-12">
						Requests must be received by 2:00 A.M. Central Time. Pickup occurs
						the next postal delivery day when your mail is delivered. Packages
						must be properly sealed and ready for shipment before your carrier
						arrives.
					</p>
					<p className="p-col-11">
						<b>Contact Information</b>
					</p>
					<WrappedInputTextWithLabel
						className="p-col-11 pickup-input-field"
						errorMessage="Full name is required."
						isInputInvalid={validations.fullName === -1}
						label="Full Name:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: fullName,
							dataqa: 'fullName',
							name: 'fullName',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-11 pickup-input-field"
						label="Company:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: companyName,
							dataqa: 'companyName',
							name: 'companyName',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-11 pickup-input-field"
						errorMessage="Address is required."
						isInputInvalid={validations.addressLine1 === -1}
						label="Address:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: addressLine1,
							dataqa: 'addressLine1',
							name: 'addressLine1',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-11 pickup-input-field"
						errorMessage="City is required."
						isInputInvalid={validations.city === -1}
						label="City:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: city,
							dataqa: 'city',
							name: 'city',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedDropdownWithLabel
						className="p-col-7 pickup-input-field"
						errorMessage="State is required."
						isInputInvalid={validations.stateProvince === -1}
						label="State:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: stateProvince,
							options: States,
							optionValue: 'abbreviation',
							optionLabel: 'name',
							dataqa: 'state',
							name: 'stateProvince',
							onBlur: this.onBlurDropdown,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-4 pickup-input-field-offset"
						errorMessage="ZIP is required."
						isInputInvalid={validations.postalCode === -1}
						label="ZIP:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: postalCode,
							dataqa: 'postalCode',
							name: 'postalCode',
							keyfilter: 'pint',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-11 pickup-input-field"
						errorMessage="Phone number is required."
						isInputInvalid={validations.phone === -1}
						label="Telephone:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: Util.formatPhoneNumber(phone),
							dataqa: 'phone',
							name: 'phone',
							keyfilter: 'pint',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<WrappedInputTextWithLabel
						className="p-col-11"
						errorMessage="Pickup Instructions are required."
						isInputInvalid={validations.pickupInstructions === -1}
						label="Pickup Instructions:"
						showErrorBelow={isMobile}
						uiElementConfig={{
							value: pickupInstructions,
							dataqa: 'pickupInstructions',
							name: 'pickupInstructions',
							onBlur: this.onBlur,
							onChange: this.onChange,
						}}
					/>
					<p className="p-col-12">
						<br />
						<b>Pickup Cost: Free</b>
					</p>
				</div>
			</>
		);
	};

	/**
	 * Render the 'PostPickup' modal view.
	 * @returns {ReactElement}
	 */
	renderPostPickupView = () => {
		const {
			addressLine1,
			city,
			companyName,
			fullName,
			numberOfItemsInPickup,
			stateProvince,
			pickupDate,
			pickupId,
			postalCode,
			pickupInstructions,
		} = this.state;

		return (
			<>
				<div className="p-grid">
					<p className="p-col-12">
						<strong>Your pickup has been scheduled.</strong>
					</p>
					<p className="p-col-12">
						Thank you for scheduling a pickup with the United States Postal
						Service!
						<br />
						Your Carrier will pickup {numberOfItemsInPickup} package(s) during
						their normal delivery time.
					</p>
					<p className="p-col-12">Your pickup has been scheduled for:</p>
					<p className="p-col-11 p-offset-1">
						{pickupDate}
						<br />
						<strong>Pickup Instructions: </strong> {pickupInstructions}
						<br />
						<strong>Confirmation Number: </strong> {pickupId}
					</p>
					<p className="p-col-11 p-offset-1">
						{fullName}
						{fullName ? <br /> : null}
						{companyName}
						{companyName ? <br /> : null}
						{addressLine1}
						{addressLine1 ? <br /> : null}
						{city}, {stateProvince} {postalCode}
					</p>
					<p className="p-col-12">
						To print a SCAN Form for these packages, click Print SCAN Form
					</p>
				</div>
			</>
		);
	};

	render() {
		const { modalId } = this.props;
		const { viewType, isSchedulePickupClicked } = this.state;
		const header = this.isEligibleForGlobalPost()
			? 'Schedule USPS & GlobalPost Pickup'
			: 'Schedule USPS Pickup';

		const primaryButton =
			viewType === ViewType.Pickup
				? {
						type: ModalButtonActionType.Ok,
						text: 'Schedule Pickup',
						onClick: this.onClickSchedulePickupButton,
						disabled: isSchedulePickupClicked,
						dataqa: 'schedule-pickup-schedule-pickup-button',
					}
				: {
						type: ModalButtonActionType.Ok,
						text: 'Print SCAN Form',
						onClick: this.onClickPrintScanForm,
						dataqa: 'schedule-pickup-pickup-scan-form',
					};

		const secondaryButton =
			viewType === ViewType.Pickup
				? {
						type: ModalButtonActionType.Cancel,
						text: 'Cancel',
					}
				: {
						type: ModalButtonActionType.Cancel,
						text: 'Done',
					};

		return (
			<Modal
				modalId={modalId}
				className="history-pickup schedule-usps-pickup-modal"
				header={header}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
				size="small"
			>
				{viewType === ViewType.Pickup
					? this.renderPickupView()
					: this.renderPostPickupView()}
			</Modal>
		);
	}
}

export default withLayoutAndModalContexts(ScheduleUspsPickupModal);
